import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumns } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'
class PointStatsInPolygons extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.pointstatsinpolygons',
        group: 'Aggregation',
        title: 'Point Stats in Polygons',
        description: `
      This component adds new columns with statistics computed from the points of a second table
      that fall within each polygon.
      `
      },
      [
        {
          name: 'points',
          type: WorkflowComponentParamType.InputTable,
          title: 'Points table'
        },
        {
          name: 'polygons',
          type: WorkflowComponentParamType.InputTable,
          title: 'Polygons table'
        },
        {
          name: 'geopoints',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in points table',
          parent: 'points',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geopolygons',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in polygons table',
          parent: 'polygons',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'statscol',
          type: WorkflowComponentParamType.Column,
          title: 'Column to compute stats for in points table',
          parent: 'points',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'idcol',
          type: WorkflowComponentParamType.Column,
          title: 'Column with identifier in polygons table',
          parent: 'polygons',
          optional: true,
          dataType: [ColumnDataType.Number, ColumnDataType.String]
        }
      ]
    )
    this.supportedProviders = [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.Postgres,
      Provider.Redshift
    ]
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Snowflake:
      case Provider.Postgres:
      case Provider.Redshift:
        return dedent`SELECT
                    polygonsb.*,
                    stat.sum_${params.statscol},
                    stat.avg_${params.statscol},
                    stat.max_${params.statscol},
                    stat.min_${params.statscol}
                FROM
                    (SELECT
                    polygons.${params.idcol} poligonid,
                    SUM(points.${params.statscol}) AS sum_${params.statscol},
                    AVG(points.${params.statscol}) AS avg_${params.statscol},
                    MIN(points.${params.statscol}) AS min_${params.statscol},
                    MAX(points.${params.statscol}) AS max_${params.statscol}
                    FROM
                    ${connection.quoteName(
                      params.polygons as string
                    )} as polygons,
                    ${connection.quoteName(params.points as string)} as points
                    WHERE
                        ST_INTERSECTS(
                          ${connection.processInputGeogColumn(
                            `polygons.${params.geopolygons}`
                          )},
                          ${connection.processInputGeogColumn(
                            `points.${params.geopoints}`
                          )}
                        )
                    GROUP BY
                        polygons.${params.idcol}
                    ) stat
                INNER JOIN ${connection.quoteName(
                  params.polygons as string
                )} polygonsb
                ON stat.poligonid = polygonsb.${params.idcol}`
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumns(columns.polygons, [
      {
        name: `sum_${params.statscol}`,
        type: ColumnDataType.Number
      },
      {
        name: `avg_${params.statscol}`,
        type: ColumnDataType.Number
      },
      {
        name: `max_${params.statscol}`,
        type: ColumnDataType.Number
      },
      {
        name: `min_${params.statscol}`,
        type: ColumnDataType.Number
      }
    ])
  }
}

export { PointStatsInPolygons }
